/**
 * Right panel editor for a single day — title, location, accommodation, events.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Plus,
  Trash2,
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  File as FileIcon,
  Copy,
  ClipboardPaste,
  GripVertical,
} from 'lucide-react';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { VaultFilePickerDialog } from '@/components/VaultFilePickerDialog';
import type {
  TravelItemResponse,
  TravelEventResponse,
  TravelTripResponse,
  TravelEventType,
  TravelAttachmentResponse,
} from '../api';
import { TravelEventCard } from './TravelEventCard';
import { TravelAttachments } from './TravelAttachments';
import { TimePicker } from './TimePicker';
import { formatDayDate, getDayDate, EVENT_TYPE_LABELS, getEventTypeColor } from './TravelHelpers';
import { TravelDatePicker } from './TravelDatePicker';
import { TravelExpenseQuickAdd } from './TravelExpenseQuickAdd';
import { useTravelBudget, useTravelFeature } from '../hooks';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return FileIcon;
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.startsWith('video/')) return Film;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType.startsWith('text/') || mimeType.includes('pdf') || mimeType.includes('document'))
    return FileText;
  return FileIcon;
}

/** Staged file ready to be attached once the event is created */
interface StagedFile {
  fileId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

function toISODate(d: Date): string {
  return d.toISOString().split('T')[0];
}

/** Parse "YYYY-MM-DD" or "YYYY-MM-DD HH:MM" → date part */
function parseCheckoutDate(value: string | null): string {
  if (!value) return '';
  return value.split(' ')[0] || '';
}

/** Parse "YYYY-MM-DD HH:MM" → time part */
function parseCheckoutTime(value: string | null): string {
  if (!value) return '';
  const parts = value.split(' ');
  return parts[1] || '';
}

/** Sortable wrapper for TravelEventCard with drag handle, copy, and move-to-day */
function SortableEventCard({
  event,
  attachments: atts,
  dayItems,
  isCopied,
  tripId,
  dayDateIso,
  onUpdate,
  onDelete,
  onCopy,
  onMove,
  onAddAttachment,
  onRemoveAttachment,
}: {
  event: TravelEventResponse;
  attachments: TravelAttachmentResponse[];
  dayItems: TravelItemResponse[];
  isCopied: boolean;
  tripId: string;
  dayDateIso: string;
  onUpdate: (eventId: string, input: Record<string, unknown>) => Promise<unknown>;
  onDelete: (eventId: string) => Promise<unknown>;
  onCopy: () => void;
  onMove: (eventId: string, targetItemId: string) => Promise<unknown>;
  onAddAttachment: (eventId: string, fileId: string) => Promise<unknown>;
  onRemoveAttachment: (attachmentId: string, documentId: string) => Promise<unknown>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: event.eventId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="group/sortable flex items-start gap-1">
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="mt-2.5 p-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <div className="flex-1 min-w-0">
        <TravelEventCard
          event={event}
          attachments={atts}
          onUpdate={onUpdate}
          onDelete={onDelete}
          onAddAttachment={onAddAttachment}
          onRemoveAttachment={onRemoveAttachment}
        />
        {/* Copy & Move actions — shown on hover of the whole event row */}
        <div className="flex items-center gap-1 mt-0.5 opacity-0 group-hover/sortable:opacity-100 focus-within:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 text-[10px] px-1.5 ${isCopied ? 'text-primary' : 'text-muted-foreground'}`}
            onClick={onCopy}
          >
            <Copy className="h-3 w-3 mr-0.5" /> {isCopied ? 'Copied' : 'Copy'}
          </Button>
          <EventExpenseQuickAdd
            tripId={tripId}
            itemId={event.itemId}
            eventId={event.eventId}
            dayDateIso={dayDateIso}
            eventTitle={event.title}
          />
          <Select value="" onValueChange={(targetItemId) => onMove(event.eventId, targetItemId)}>
            <SelectTrigger className="h-6 w-auto gap-0.5 text-[10px] text-muted-foreground border-none shadow-none px-1.5">
              <SelectValue placeholder="Move to..." />
            </SelectTrigger>
            <SelectContent>
              {dayItems
                .filter((d) => d.itemId !== event.itemId)
                .map((d) => (
                  <SelectItem key={d.itemId} value={d.itemId}>
                    {d.dayTitle || `Day ${d.dayNumber}`}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

interface TravelDayEditorProps {
  trip: TravelTripResponse;
  item: TravelItemResponse;
  allItems: TravelItemResponse[];
  events: TravelEventResponse[];
  attachments: TravelAttachmentResponse[];
  scheduleTopRef?: React.RefObject<HTMLDivElement | null>;
  sortDisabled?: boolean;
  copiedEventId: string | null;
  onCopyEvent: (eventId: string | null) => void;
  onPasteEvent: () => Promise<unknown>;
  onUpdateItem: (itemId: string, input: Record<string, unknown>) => Promise<unknown>;
  onDeleteItem: (itemId: string) => Promise<unknown>;
  onAddEvent: (itemId: string, input: Record<string, unknown>) => Promise<unknown>;
  onUpdateEvent: (eventId: string, input: Record<string, unknown>) => Promise<unknown>;
  onDeleteEvent: (eventId: string) => Promise<unknown>;
  onReorderEvents: (itemId: string, eventIds: string[]) => Promise<unknown>;
  onMoveEvent: (eventId: string, targetItemId: string) => Promise<unknown>;
  onDuplicateEvent: (eventId: string, targetItemId: string) => Promise<unknown>;
  onUpdateTrip: (input: { startDate?: string }) => Promise<unknown>;
  onAddItemAttachment: (itemId: string, fileId: string, label?: string) => Promise<unknown>;
  onAddEventAttachment: (eventId: string, fileId: string, label?: string) => Promise<unknown>;
  onRemoveAttachment: (attachmentId: string, documentId: string) => Promise<unknown>;
}

export function TravelDayEditor({
  trip,
  item,
  allItems,
  events,
  attachments,
  scheduleTopRef,
  sortDisabled = false,
  copiedEventId,
  onCopyEvent,
  onPasteEvent,
  onUpdateItem,
  onDeleteItem,
  onAddEvent,
  onUpdateEvent,
  onDeleteEvent,
  onReorderEvents: _onReorderEvents,
  onMoveEvent,
  onDuplicateEvent: _onDuplicateEvent,
  onUpdateTrip,
  onAddItemAttachment,
  onAddEventAttachment,
  onRemoveAttachment,
}: TravelDayEditorProps) {
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEventTime, setNewEventTime] = useState('');
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventType, setNewEventType] = useState<TravelEventType>('activity');
  const [newEventNotes, setNewEventNotes] = useState('');
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [showStagingPicker, setShowStagingPicker] = useState(false);

  // Ctrl+V pastes the copied event into the current day
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && copiedEventId) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable)
          return;
        e.preventDefault();
        onPasteEvent();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [copiedEventId, onPasteEvent]);

  const eventIds = useMemo(() => events.map((e) => e.eventId), [events]);

  // Day items for the move/paste dropdown
  const dayItems = allItems.filter((i) => i.itemType === 'day');

  // Inline editing of day fields
  const updateField = (field: string, value: string | null) => {
    onUpdateItem(item.itemId, { [field]: value });
  };

  // Auto-fill accommodation on intermediate days when checkout date is picked
  const autoFillAccommodation = (checkoutDate: string, checkoutValue: string | null) => {
    if (!checkoutDate || !item.accommodationName) return;
    const currentDayDate = getDayDate(trip.startDate, item.dayNumber ?? 1);
    const checkout = new Date(checkoutDate + 'T00:00:00');
    const dayItems = allItems
      .filter((i) => i.itemType === 'day' && i.itemId !== item.itemId)
      .filter((i) => {
        const d = getDayDate(trip.startDate, i.dayNumber ?? 1);
        return d > currentDayDate && d < checkout;
      });

    for (const dayItem of dayItems) {
      onUpdateItem(dayItem.itemId, {
        accommodationName: item.accommodationName,
        accommodationAddress: item.accommodationAddress,
        accommodationCheckin: null,
        accommodationCheckout: checkoutValue,
      });
    }
  };

  const handleAddEvent = async () => {
    if (!newEventTitle.trim()) return;
    const result = await onAddEvent(item.itemId, {
      time: newEventTime || null,
      title: newEventTitle,
      eventType: newEventType,
      notes: newEventNotes || null,
    });
    // Attach any staged files to the newly created event
    const newEventId = (result as { event: { eventId: string } })?.event?.eventId;
    if (newEventId && stagedFiles.length > 0) {
      for (const sf of stagedFiles) {
        try {
          await onAddEventAttachment(newEventId, sf.fileId);
        } catch {
          // best-effort — event was created, attachment failed
        }
      }
    }
    setNewEventTime('');
    setNewEventTitle('');
    setNewEventType('activity');
    setNewEventNotes('');
    setStagedFiles([]);
    setShowAddEvent(false);
  };

  const handleAddEventKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddEvent();
    }
    if (e.key === 'Escape') {
      setShowAddEvent(false);
    }
  };

  const [editingDayTitle, setEditingDayTitle] = useState(false);
  const [dayTitleDraft, setDayTitleDraft] = useState('');
  const dayTitleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingDayTitle && dayTitleRef.current) {
      dayTitleRef.current.focus();
      dayTitleRef.current.select();
    }
  }, [editingDayTitle]);

  const startEditingDayTitle = () => {
    setDayTitleDraft(item.dayTitle || '');
    setEditingDayTitle(true);
  };

  const saveDayTitle = () => {
    const trimmed = dayTitleDraft.trim();
    if (trimmed !== (item.dayTitle || '')) {
      updateField('dayTitle', trimmed || null);
    }
    setEditingDayTitle(false);
  };

  const dayDate = formatDayDate(trip.startDate, item.dayNumber ?? 1);

  // Stats
  const uniqueCities = new Set(events.length > 0 ? [item.city].filter(Boolean) : []);
  const uniqueCountries = new Set(events.length > 0 ? [item.country].filter(Boolean) : []);

  return (
    <div className="space-y-4">
      {/* Date + Day Title heading */}
      <div className="space-y-1">
        <TravelDatePicker
          value={trip.startDate}
          onChange={(newDate) => onUpdateTrip({ startDate: newDate })}
        >
          <button className="text-base font-semibold text-[#2563eb] dark:text-[#38bdf8] hover:underline cursor-pointer">
            {dayDate}
          </button>
        </TravelDatePicker>
        {editingDayTitle ? (
          <Input
            ref={dayTitleRef}
            value={dayTitleDraft}
            onChange={(e) => setDayTitleDraft(e.target.value)}
            onBlur={saveDayTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveDayTitle();
              if (e.key === 'Escape') setEditingDayTitle(false);
            }}
            placeholder={`Day ${item.dayNumber}`}
            className="text-3xl font-bold border-none shadow-none px-0 h-auto focus-visible:ring-0"
          />
        ) : (
          <h1
            className="text-3xl font-bold cursor-pointer hover:opacity-70 transition-opacity"
            onClick={startEditingDayTitle}
          >
            {item.dayTitle || `Day ${item.dayNumber}`}
          </h1>
        )}
      </div>

      {/* Locations */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Locations</h3>
        </div>
        {/* Primary location */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground mb-1 block">City</label>
            <Input
              value={item.city || ''}
              onChange={(e) => updateField('city', e.target.value || null)}
              placeholder="City"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground mb-1 block">Country</label>
            <Input
              value={item.country || ''}
              onChange={(e) => updateField('country', e.target.value || null)}
              placeholder="Country"
            />
          </div>
          <span className="text-[10px] text-muted-foreground pb-2.5 shrink-0">primary</span>
        </div>
        {/* Additional locations */}
        {(item.locations || []).map((loc, idx) => (
          <div key={idx} className="flex gap-2 items-end mt-2">
            <div className="flex-1">
              {idx === 0 && (
                <label className="text-xs text-muted-foreground mb-1 block">City</label>
              )}
              <Input
                value={loc.city}
                onChange={(e) => {
                  const updated = [...(item.locations || [])];
                  updated[idx] = { ...updated[idx], city: e.target.value };
                  onUpdateItem(item.itemId, { locations: updated });
                }}
                placeholder="City"
              />
            </div>
            <div className="flex-1">
              {idx === 0 && (
                <label className="text-xs text-muted-foreground mb-1 block">Country</label>
              )}
              <Input
                value={loc.country || ''}
                onChange={(e) => {
                  const updated = [...(item.locations || [])];
                  updated[idx] = { ...updated[idx], country: e.target.value || undefined };
                  onUpdateItem(item.itemId, { locations: updated });
                }}
                placeholder="Country"
              />
            </div>
            <button
              className="pb-2.5 text-muted-foreground hover:text-destructive transition-colors shrink-0"
              onClick={() => {
                const updated = (item.locations || []).filter((_, i) => i !== idx);
                onUpdateItem(item.itemId, { locations: updated });
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => {
            const updated = [...(item.locations || []), { city: '', country: '' }];
            onUpdateItem(item.itemId, { locations: updated });
          }}
        >
          <Plus className="h-3 w-3 mr-1" /> Add Location
        </Button>
      </div>

      {/* Accommodation */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Accommodation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            value={item.accommodationName || ''}
            onChange={(e) => updateField('accommodationName', e.target.value || null)}
            placeholder="Hotel name"
          />
          <Input
            value={item.accommodationAddress || ''}
            onChange={(e) => updateField('accommodationAddress', e.target.value || null)}
            placeholder="Address"
          />
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Check-in time</label>
              <TimePicker
                value={item.accommodationCheckin || ''}
                onChange={(v) => updateField('accommodationCheckin', v || null)}
                placeholder="Check-in"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Check-out date</label>
              <Input
                type="date"
                value={parseCheckoutDate(item.accommodationCheckout)}
                min={toISODate(getDayDate(trip.startDate, item.dayNumber ?? 1))}
                onChange={(e) => {
                  const checkoutDate = e.target.value;
                  const time = parseCheckoutTime(item.accommodationCheckout);
                  const combined = checkoutDate
                    ? time
                      ? `${checkoutDate} ${time}`
                      : checkoutDate
                    : null;
                  updateField('accommodationCheckout', combined);
                  autoFillAccommodation(checkoutDate, combined);
                }}
              />
            </div>
            <div className="w-32">
              <label className="text-xs text-muted-foreground mb-1 block">Check-out time</label>
              <TimePicker
                value={parseCheckoutTime(item.accommodationCheckout)}
                onChange={(time) => {
                  const date = parseCheckoutDate(item.accommodationCheckout);
                  const combined = date ? (time ? `${date} ${time}` : date) : time || null;
                  updateField('accommodationCheckout', combined);
                }}
                placeholder="Check-out"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events / Schedule */}
      <div ref={scheduleTopRef}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Schedule</h3>
          <span className="text-xs text-muted-foreground">{events.length} events</span>
        </div>
        <SortableContext
          items={eventIds}
          strategy={sortDisabled ? undefined : verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {events.map((event) => (
              <SortableEventCard
                key={event.eventId}
                event={event}
                attachments={attachments.filter((a) => a.eventId === event.eventId)}
                dayItems={dayItems}
                isCopied={copiedEventId === event.eventId}
                tripId={trip.tripId}
                dayDateIso={getDayDate(trip.startDate, item.dayNumber ?? 1)
                  .toISOString()
                  .slice(0, 10)}
                onUpdate={onUpdateEvent}
                onDelete={onDeleteEvent}
                onCopy={() => onCopyEvent(event.eventId)}
                onMove={onMoveEvent}
                onAddAttachment={onAddEventAttachment}
                onRemoveAttachment={onRemoveAttachment}
              />
            ))}
          </div>
        </SortableContext>

        {showAddEvent ? (
          <div
            className="mt-2 rounded-md border-2 border-blue-400/60 bg-card p-3 space-y-2"
            onKeyDown={handleAddEventKeyDown}
          >
            <div className="flex gap-2">
              <TimePicker
                value={newEventTime}
                onChange={setNewEventTime}
                placeholder="Time"
                className="w-32"
              />
              <Input
                value={newEventTitle}
                onChange={(e) => setNewEventTitle(e.target.value)}
                placeholder="Event title"
                className="flex-1"
                autoFocus
              />
            </div>
            <Select
              value={newEventType}
              onValueChange={(v) => setNewEventType(v as TravelEventType)}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(EVENT_TYPE_LABELS) as TravelEventType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: getEventTypeColor(type) }}
                    />
                    {EVENT_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              value={newEventNotes}
              onChange={(e) => setNewEventNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={2}
            />
            {/* Attachments — staged files queued before event is created */}
            <div className="space-y-2 border-t border-border pt-2">
              <span className="text-xs font-medium text-muted-foreground">Attachments</span>
              {stagedFiles.length > 0 && (
                <div className="space-y-1">
                  {stagedFiles.map((sf) => {
                    const Icon = getFileIcon(sf.mimeType);
                    return (
                      <div
                        key={sf.fileId}
                        className="group/sf flex items-center gap-2 rounded border border-border px-2 py-1.5 text-sm"
                      >
                        <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{sf.fileName}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatFileSize(sf.fileSize)}
                        </span>
                        <button
                          onClick={() =>
                            setStagedFiles((prev) => prev.filter((f) => f.fileId !== sf.fileId))
                          }
                          className="p-0.5 rounded hover:bg-muted text-muted-foreground shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setShowStagingPicker(true)}
                >
                  <Paperclip className="h-3 w-3 mr-1" /> Attach file
                </Button>
              </div>
            </div>
            <VaultFilePickerDialog
              open={showStagingPicker}
              onOpenChange={setShowStagingPicker}
              title="Attach a File"
              description="Pick a file from your Vault, or upload a new one to attach."
              confirmLabel="Attach"
              onFileSelected={(picked) => {
                setStagedFiles((prev) => [
                  ...prev,
                  {
                    fileId: picked.documentId,
                    fileName: picked.fileName,
                    fileSize: picked.sizeBytes,
                    mimeType: picked.mimeType || 'application/octet-stream',
                  },
                ]);
              }}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddEvent} disabled={!newEventTitle.trim()}>
                Add Event
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowAddEvent(false);
                  setStagedFiles([]);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setShowAddEvent(true)}>
              <Plus className="h-3 w-3 mr-1" /> Add Event
            </Button>
            {copiedEventId && (
              <Button variant="outline" size="sm" onClick={() => onPasteEvent()}>
                <ClipboardPaste className="h-3 w-3 mr-1" /> Paste Event
              </Button>
            )}
            <DayExpenseQuickAdd
              tripId={trip.tripId}
              itemId={item.itemId}
              dayDateIso={getDayDate(trip.startDate, item.dayNumber ?? 1)
                .toISOString()
                .slice(0, 10)}
            />
          </div>
        )}
      </div>

      {/* Day-level attachments */}
      <div>
        <h3 className="text-sm font-semibold mb-2">Attachments</h3>
        <TravelAttachments
          attachments={attachments.filter((a) => a.itemId === item.itemId)}
          onAddAttachment={(fileId) => onAddItemAttachment(item.itemId, fileId)}
          onRemoveAttachment={onRemoveAttachment}
        />
      </div>

      {/* Stats bar */}
      <div className="flex gap-4 text-xs text-muted-foreground border-t border-border pt-3">
        <span>{events.length} events</span>
        {uniqueCities.size > 0 && <span>{uniqueCities.size} city</span>}
        {uniqueCountries.size > 0 && <span>{uniqueCountries.size} country</span>}
      </div>

      {/* Delete day */}
      <Button
        variant="outline"
        size="sm"
        className="text-destructive border-destructive/30 hover:bg-destructive/10"
        onClick={() => onDeleteItem(item.itemId)}
      >
        <Trash2 className="h-3 w-3 mr-1" /> Remove Day
      </Button>
    </div>
  );
}

/**
 * Small feature-gated wrapper around TravelExpenseQuickAdd that hooks
 * into useTravelBudget for the submit path. Lives here (not inside
 * TravelBudgetPanel) so the day editor can trigger a quick-add
 * pre-filled with the current day's id + date.
 */
/** Event-level expense quick-add. Pre-fills description from the event's
 * title and scopes the expense to `eventId`. Hidden when the budget
 * feature is off. */
function EventExpenseQuickAdd({
  tripId,
  itemId,
  eventId,
  dayDateIso,
  eventTitle,
}: {
  tripId: string;
  itemId: string;
  eventId: string;
  dayDateIso: string;
  eventTitle: string;
}) {
  const feature = useTravelFeature('budget');
  const { budget, createExpense, isCreatingExpense } = useTravelBudget(tripId);
  if (!feature.enabled) return null;
  const home = budget?.homeCurrency ?? 'USD';
  return (
    <TravelExpenseQuickAdd
      trigger={
        <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5 text-muted-foreground">
          + Expense
        </Button>
      }
      defaultItemId={itemId}
      defaultEventId={eventId}
      defaultIncurredAt={dayDateIso}
      defaultCurrency={home}
      onSubmit={(input) =>
        createExpense({ ...input, description: input.description ?? eventTitle })
      }
      isSubmitting={isCreatingExpense}
    />
  );
}

function DayExpenseQuickAdd({
  tripId,
  itemId,
  dayDateIso,
}: {
  tripId: string;
  itemId: string;
  dayDateIso: string;
}) {
  const feature = useTravelFeature('budget');
  const { budget, createExpense, isCreatingExpense } = useTravelBudget(tripId);
  if (!feature.enabled) return null;
  const home = budget?.homeCurrency ?? 'USD';
  return (
    <TravelExpenseQuickAdd
      trigger={
        <Button variant="outline" size="sm">
          <Plus className="h-3 w-3 mr-1" /> Add Expense
        </Button>
      }
      defaultItemId={itemId}
      defaultEventId={null}
      defaultIncurredAt={dayDateIso}
      defaultCurrency={home}
      onSubmit={(input) => createExpense(input)}
      isSubmitting={isCreatingExpense}
    />
  );
}
